import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { LoginService } from '../../services/login.service';
import { User, UserRole } from '../../models/user';
import { MenuData, MenuItem, MenuSection } from '../../models/menu-data';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-dashboard-menu',
  imports: [CommonModule, RouterModule],
  templateUrl: './dashboard-menu.component.html',
  styleUrl: './dashboard-menu.component.css'
})
export class DashboardMenuComponent implements OnInit, OnDestroy {

  @Input() menuData!: MenuData;

  // consider making user an input
  public user!: User;

  UserRole = UserRole;

  // Dropdown (desktop) and slide-down panel (small screens) state.
  userMenuOpen: boolean = false;
  mobileMenuOpen: boolean = false;

  // Shown in place of the logo on small screens; comes from the route's data.pageTitle.
  pageTitle: string = '';

  // Back arrow (small screens): hidden on the shell's home page.
  showBack: boolean = false;
  private backRoute: string = '';

  private destroy$ = new Subject<void>();

  constructor(public loginService: LoginService, private router: Router, private location: Location, private host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    this.user = this.loginService.user;
    this.updateRouteState();

    // Any navigation closes whatever is open and refreshes the page title.
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.closeAll();
      this.updateRouteState();
    });
  }

  // Deepest route with a pageTitle / backRoute wins, so child routes can override their parent.
  private updateRouteState(): void {
    let title = '';
    let back = '';
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (snapshot) {
      if (snapshot.data?.['pageTitle']) title = snapshot.data['pageTitle'];
      if (snapshot.data?.['backRoute']) back = snapshot.data['backRoute'];
      snapshot = snapshot.firstChild;
    }
    this.pageTitle = title;
    this.backRoute = back;

    const path = this.router.url.split(/[?#]/)[0];
    const shellRoot = '/' + (this.homeRoute.split('/')[1] ?? '');
    this.showBack = path !== this.homeRoute && path !== shellRoot;
  }

  goBack(): void {
    // Angular stamps each in-app history entry with an increasing navigationId; 1 means this is
    // the first entry of the session (deep link / refresh), so history.back() would leave the app.
    const navigationId = (this.location.getState() as { navigationId?: number } | null)?.navigationId ?? 1;
    if (navigationId > 1) {
      this.location.back();
    } else {
      this.router.navigateByUrl(this.backRoute || this.homeRoute);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // The first section stays inline in the bar, everything else collapses into the user dropdown.
  get primaryItems(): MenuItem[] {
    return this.menuData?.sections?.[0]?.items ?? [];
  }

  get secondarySections(): MenuSection[] {
    return this.menuData?.sections?.slice(1) ?? [];
  }

  get homeRoute(): string {
    return this.primaryItems[0]?.route ?? '';
  }

  toggleUserMenu(): void {
    this.userMenuOpen = !this.userMenuOpen;
    this.mobileMenuOpen = false;
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.userMenuOpen = false;
  }

  closeAll(): void {
    this.userMenuOpen = false;
    this.mobileMenuOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.closeAll();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeAll();
  }
}
